/**
 * MHR Context
 *
 * Provides MHR-specific state and user management.
 * Works alongside the main AuthContext from the SiteOperations app.
 */

import { createContext, useContext, useEffect, useState, ReactNode, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import * as mhrApi from '../lib/api';
import type { Protocol, MHRUser, PatientWithStats } from '../types';

interface MHRContextType {
  user: MHRUser | null;
  loading: boolean;
  role: 'admin' | 'user' | null;
  isAdmin: boolean;

  // Protocols
  protocols: Protocol[];
  protocolsLoading: boolean;
  currentProtocol: Protocol | null;
  setCurrentProtocol: (protocol: Protocol | null) => void;
  loadProtocols: (force?: boolean) => Promise<void>;
  refreshProtocol: (protocolId: string) => Promise<Protocol | null>;

  // Patients
  patients: PatientWithStats[];
  patientsLoading: boolean;
  currentPatient: PatientWithStats | null;
  setCurrentPatient: (patient: PatientWithStats | null) => void;
  loadPatients: (force?: boolean) => Promise<void>;

  // Error state
  error: string | null;
  clearError: () => void;
}

const MHRContext = createContext<MHRContextType | undefined>(undefined);

export function MHRProvider({ children }: { children: ReactNode }) {
  const { user: authUser, currentSite } = useAuth();
  const [user, setUser] = useState<MHRUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState<'admin' | 'user' | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Protocols state
  const [protocols, setProtocols] = useState<Protocol[]>([]);
  const [protocolsLoading, setProtocolsLoading] = useState(false);
  const [currentProtocol, setCurrentProtocol] = useState<Protocol | null>(null);

  // Patients state
  const [patients, setPatients] = useState<PatientWithStats[]>([]);
  const [patientsLoading, setPatientsLoading] = useState(false);
  const [currentPatient, setCurrentPatient] = useState<PatientWithStats | null>(null);

  const isAdmin = role === 'admin';

  // Track if initial data has been fetched
  const initialFetchDone = useRef(false);
  const lastAuthUserId = useRef<string | null>(null);
  const lastSiteId = useRef<string | null>(null);
  const protocolsLoaded = useRef(false);
  const patientsLoaded = useRef(false);

  // Clear error
  const clearError = useCallback(() => setError(null), []);

  // Load user role for MHR tool
  useEffect(() => {
    // Guard against a fast site switch A->B where A's slower response resolves
    // last: a superseded run must not overwrite role/user with the wrong site's
    // values, nor poison the dedupe cache (refs) it never legitimately loaded.
    let cancelled = false;

    const loadUserRole = async () => {
      if (!authUser || !currentSite) {
        setUser(null);
        setRole(null);
        setLoading(false);
        return;
      }

      // Skip if we already loaded for this user/site combination
      if (
        lastAuthUserId.current === authUser.id &&
        lastSiteId.current === currentSite.id &&
        initialFetchDone.current
      ) {
        return;
      }

      try {
        // Fetch MHR role from tool_user_roles table via API
        const roleResponse = await api.get(`/api/my-tool-role/mhr?site_id=${currentSite.id}`);
        if (cancelled) return;
        const mhrRole = roleResponse.data?.role as 'admin' | 'user' | null;

        setRole(mhrRole);
        setUser({
          id: authUser.id,
          email: authUser.email,
          full_name: authUser.full_name,
          avatar_url: authUser.avatar_url ?? null,
          site_id: currentSite.id,
          role: mhrRole,
        });
        // Commit the dedupe cache only on a non-cancelled success, so a
        // superseded run can never mark this user/site combination as loaded.
        lastAuthUserId.current = authUser.id;
        lastSiteId.current = currentSite.id;
        initialFetchDone.current = true;
      } catch (err) {
        if (cancelled) return;
        console.error('[MHRContext] Error loading user role:', err);
        setRole(null);
        setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadUserRole();

    return () => {
      cancelled = true;
    };
  }, [authUser, currentSite]);

  // Load protocols
  const loadProtocols = useCallback(
    async (force = false) => {
      if (!currentSite) {
        setProtocols([]);
        return;
      }

      // Skip if already loaded and not forcing
      if (protocolsLoaded.current && !force) {
        return;
      }

      setProtocolsLoading(true);
      try {
        // Include all statuses except 'closed' by default
        const result = await mhrApi.listProtocols({
          status: ['setup', 'active', 'paused'],
          orderBy: 'updated_at',
          orderDirection: 'desc',
        });
        setProtocols(result.data);
        protocolsLoaded.current = true;
      } catch (err: any) {
        console.error('[MHRContext] Error loading protocols:', err);
        setError(err.response?.data?.error || 'Failed to load protocols');
        setProtocols([]);
      } finally {
        setProtocolsLoading(false);
      }
    },
    [currentSite]
  );

  // Load patients
  const loadPatients = useCallback(
    async (force = false) => {
      if (!currentSite) {
        setPatients([]);
        return;
      }

      if (patientsLoaded.current && !force) {
        return;
      }

      setPatientsLoading(true);
      try {
        const result = await mhrApi.listPatientsWithStats({ limit: 100 });
        setPatients(result.data);
        patientsLoaded.current = true;
      } catch (err: any) {
        console.error('[MHRContext] Error loading patients:', err);
      } finally {
        setPatientsLoading(false);
      }
    },
    [currentSite]
  );

  // Refresh a single protocol
  const refreshProtocol = useCallback(async (protocolId: string): Promise<Protocol | null> => {
    try {
      const protocol = await mhrApi.getProtocol(protocolId);

      // Update in list
      setProtocols(prev => prev.map(p => (p.id === protocolId ? protocol : p)));

      // Update current if it's the same
      if (currentProtocol?.id === protocolId) {
        setCurrentProtocol(protocol);
      }

      return protocol;
    } catch (err: any) {
      console.error('[MHRContext] Error refreshing protocol:', err);
      setError(err.response?.data?.error || 'Failed to refresh protocol');
      return null;
    }
  }, [currentProtocol]);

  // Load protocols when site changes
  useEffect(() => {
    if (currentSite && !loading) {
      if (!protocolsLoaded.current || lastSiteId.current !== currentSite.id) {
        loadProtocols(true);
      }
    }
  }, [currentSite, loading, loadProtocols]);

  // Reset state when site changes
  useEffect(() => {
    if (currentSite && lastSiteId.current && lastSiteId.current !== currentSite.id) {
      protocolsLoaded.current = false;
      patientsLoaded.current = false;
      setCurrentProtocol(null);
      setCurrentPatient(null);
    }
  }, [currentSite]);

  return (
    <MHRContext.Provider
      value={{
        user,
        loading,
        role,
        isAdmin,
        protocols,
        protocolsLoading,
        currentProtocol,
        setCurrentProtocol,
        loadProtocols,
        refreshProtocol,
        patients,
        patientsLoading,
        currentPatient,
        setCurrentPatient,
        loadPatients,
        error,
        clearError,
      }}
    >
      {children}
    </MHRContext.Provider>
  );
}

export function useMHR() {
  const context = useContext(MHRContext);
  if (context === undefined) {
    throw new Error('useMHR must be used within an MHRProvider');
  }
  return context;
}
